import { useEffect, useState } from 'react'

// static cache for fast resolution once the font is loaded.
// this assumes that fonts will never get unloaded.
const FontsLoaded: Record<string, boolean> = {}

// static cache for loading fonts.
const FontsLoading: Record<string, Promise<void>> = {}

export const useIsFontLoaded = (fontName: string) => {
  const [isFontLoaded, setIsFontLoaded] = useState(FontsLoaded[fontName])

  useEffect(() => {
    if (FontsLoaded[fontName]) {
      setIsFontLoaded(true)
      return
    }

    if (!hasFontsAPI()) {
      // If no fonts API, we assume the font is loaded
      FontsLoaded[fontName] = true
      setIsFontLoaded(true)
      return
    }

    // reset the state if fontName has changed
    setIsFontLoaded(false)

    if (!FontsLoading[fontName]) {
      FontsLoading[fontName] = loadFont(fontName)
    }

    FontsLoading[fontName].then(() => {
      setIsFontLoaded(true)
    })
  }, [fontName])

  return isFontLoaded
}

const loadFont = (fontName: string): Promise<void> => {
  return new Promise((resolve) => {
    async function load() {
      // wait for fonts to load
      await document.fonts.ready

      const isLoaded = document.fonts.check(`1em "${fontName}"`)
      if (isLoaded) {
        FontsLoaded[fontName] = true
        return
      }

      // load the font
      await document.fonts.load(`1em "${fontName}"`) // will throw error if fails
      FontsLoaded[fontName] = true
    }

    load().then(resolve)
  })
}

const hasFontsAPI = () => {
  return !!(
    typeof document !== 'undefined' &&
    document.fonts &&
    typeof document.fonts.ready === 'object' &&
    typeof document.fonts.check === 'function'
  )
}
